/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file hook to get a function that will audit the use of a feature
 * @author Colin Walters
 * @module Epic.VideoApp.Hooks.Auditing.UseAuditFeatureUse
 */

import { useCallback } from "react";
import { useAuthState } from "~/state";
import { EventFlag, EventType } from "~/types";
import { makeRequest } from "~/utils/request";

type FeatureAudit = AuditEvent[];
/**
 * Get a function to audit feature use
 * @returns a function to audit feature usage
 */
export function useAuditFeatureUse(): (featureInput: FeatureAudit) => Promise<void> {
	const jwt = useAuthState((selectors) => selectors.getJWT(), []);

	const auditUse = useCallback(
		async (audit: FeatureAudit) => {
			// if room.isDisconnecting is used to not log events, ensure it works with screen sharing stopped
			// being audited in useScreenShareTrackActions.removeScreenShareTrack when called via useDisconnect
			if (!jwt) {
				return;
			}

			try {
				await auditFeatureUse(jwt, audit);
			} catch {
				// don't allow feature use auditing to crash the client
			}
		},
		[jwt],
	);

	return auditUse;
}

interface IAuditParams {
	/** timestamp that the action occurred, defaults to current time */
	timestamp?: number;
	/** The unique participant identifier that an event targets. This should correspond the the Twilio identity field (session.UniqueUserId) */
	targetParticipantId?: string;
}

type AuditEvent = IBasicAuditEvent | IScreenShareAuditEvent;

export interface IBasicAuditEvent extends IAuditParams {
	feature: EventType;
}

export interface IScreenShareAuditEvent extends IBasicAuditEvent {
	feature: EventType.screenSharingStarted | EventType.screenSharingStopped;
	eventFlags: EventFlag.hasScreenShareAudio[];
}

/**
 * Make a web server call to audit a feature use for the current user
 * @param jwt current session JWT
 * @param feature feature to audit
 * @param params additional details about the feature being audited
 */
async function auditFeatureUse(jwt: string, audit: FeatureAudit): Promise<void> {
	return makeRequest("/api/VideoCall/AuditFeatureUse", "POST", jwt, { events: audit });
}
